import { BaseEntity, type EntityDeleteOptions, type EntitySaveOptions, type ValidationResult } from '@memberjunction/core';
import { RegisterClass } from '@memberjunction/global';
import { FeedbackPlatformCommitteeSelectionEntity, FeedbackPlatformCommitteeSelectionItemEntity } from 'mj_generatedentities';
import { DecideParentReadable, DecideSelectionChange, DecideSelectionItemAdd, DecideSelectionItemRemove, type RuleDecision } from '../rules/decisions.js';
import { CreateOEPRuleData } from '../rules/data.js';
import { IsStaffOrSystem } from '../rules/user.js';
import { OEP_ENTITY } from '../contracts/entities.js';
import { Apply, BeginRules, CurrentString, TrustedID } from './shared.js';

@RegisterClass(BaseEntity, OEP_ENTITY.CommitteeSelections)
export class OEPCommitteeSelectionEntity extends FeedbackPlatformCommitteeSelectionEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'CommitteeSelections', this.ID, {
            SeasonID: this.SeasonID, SectionID: this.SectionID, SubmittedByUserID: this.SubmittedByUserID,
        }, ['Status'], ['SubmittedByUserID']);
        if (!rc) return result;
        const seasonID = TrustedID(rc, 'SeasonID', this.SeasonID);
        const season = await rc.Data.SeasonPhase(seasonID);
        Apply(result, DecideParentReadable({ IsStaffOrSystem: rc.IsStaff, Found: season !== null, Field: 'SeasonID' }), seasonID);
        if (!season) return result;
        const old = CurrentString(rc, 'Status');
        Apply(result, DecideSelectionChange({
            Phase: season.Phase, IsStaffOrSystem: rc.IsStaff,
            // Old status from the fresh DB row, not the client-supplied OldValue.
            OldStatus: old === 'Draft' || old === 'Submitted' ? old : null,
            NewStatus: this.Status, ItemCount: this.IsSaved ? await rc.Data.SelectionItemCount(this.ID) : 0,
        }), this.Status);
        return result;
    }
}

@RegisterClass(BaseEntity, OEP_ENTITY.CommitteeSelectionItems)
export class OEPCommitteeSelectionItemEntity extends FeedbackPlatformCommitteeSelectionItemEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }

    /** Result of the add decision for the insert in flight; Save computes it once and ValidateAsync reuses it. */
    private pendingAdd: Promise<RuleDecision<{ Slot: number }> | null> | null = null;

    /** Null = staff/system with an unreadable slate or season (their insert is not rule-checked). */
    private async decideAdd(): Promise<RuleDecision<{ Slot: number }> | null> {
        const user = this.ActiveUser;
        const data = CreateOEPRuleData(this.RunViewProviderToUse, user);
        const sel = await data.Selection(this.CommitteeSelectionID);
        const season = sel ? await data.SeasonPhase(sel.SeasonID) : null;
        if (!sel || !season) {
            const readable = DecideParentReadable({ IsStaffOrSystem: IsStaffOrSystem(user), Found: false, Field: 'CommitteeSelectionID' });
            return readable.Ok ? null : { Ok: false, Field: readable.Field, Message: readable.Message };
        }
        return DecideSelectionItemAdd({
            Phase: season.Phase, SelectionStatus: sel.Status, UsedSlots: await data.SelectionSlots(this.CommitteeSelectionID),
            Cap: season.WinnersPerSection, PresentationHasSubmittedNomination: await data.PresentationHasSubmittedNomination(this.PresentationID),
            SelectionSectionID: sel.SectionID, PresentationSectionID: await data.PresentationSectionID(this.PresentationID),
        });
    }

    /** Assigns the lowest free slot server-side; UQ_CommitteeSelectionItem_Selection_Slot makes a race fail one insert. */
    override async Save(options?: EntitySaveOptions): Promise<boolean> {
        if (this.IsSaved) return super.Save(options);
        this.pendingAdd = this.decideAdd();
        try {
            const d = await this.pendingAdd;
            if (d?.Ok) this.Slot = d.Value.Slot;
            return await super.Save(options);
        } finally {
            this.pendingAdd = null;
        }
    }

    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'CommitteeSelectionItems', this.ID, {
            CommitteeSelectionID: this.CommitteeSelectionID, PresentationID: this.PresentationID, Slot: this.Slot,
        });
        if (!rc || this.IsSaved) return result; // updates keep only the immutable-column guard
        const d = await (this.pendingAdd ?? this.decideAdd());
        if (d) Apply(result, d, this.PresentationID);
        return result;
    }

    /** Deselect: the delete resolver loads the row from the DB first, so these values are the stored ones. */
    override async Delete(options?: EntityDeleteOptions): Promise<boolean> {
        const user = this.ActiveUser;
        if (!IsStaffOrSystem(user)) {
            const data = CreateOEPRuleData(this.RunViewProviderToUse, user);
            const sel = await data.Selection(this.CommitteeSelectionID);
            const season = sel ? await data.SeasonPhase(sel.SeasonID) : null;
            if (!sel || !season) {
                const readable = DecideParentReadable({ IsStaffOrSystem: false, Found: false, Field: 'CommitteeSelectionID' });
                throw new Error(readable.Ok ? 'unreachable' : readable.Message);
            }
            const d = DecideSelectionItemRemove({ Phase: season.Phase, SelectionStatus: sel.Status, IsStaffOrSystem: false });
            if (!d.Ok) throw new Error(d.Message);
        }
        return super.Delete(options);
    }
}
